import { useCallback, useRef, useState } from 'react';
import { transcribeAudio } from '../services/openai.js';

// iOS Safari falsely reports webm support — isTypeSupported returns true
// for audio/webm but the resulting container is malformed and Whisper
// reports duration: 0. Force mp4 on iOS; iOS MediaRecorder produces
// valid mp4/AAC. Desktop and Android prefer webm/opus (smaller files).
const MIME_PREFERENCE = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function isIOS() {
  return (
    typeof navigator !== 'undefined' &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  if (isIOS()) {
    // Skip the support check — iOS lies about webm. Force mp4.
    console.log('[Recorder] iOS detected — forcing audio/mp4');
    return 'audio/mp4';
  }
  for (const m of MIME_PREFERENCE) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {}
  }
  return ''; // browser default
}

function getExtension(mimeType) {
  if (!mimeType) return 'mp4';
  if (mimeType.includes('mp4')) return 'm4a'; // iOS mp4 audio → m4a for Whisper
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('mpeg')) return 'mp3';
  return 'mp4';
}

// useWhisperRecorder
//
// MediaRecorder + Whisper transcription wrapper.
//
// Options:
//   getStream?: () => Promise<MediaStream> | MediaStream
//     If provided, the hook reuses this stream (we don't stop its tracks
//     when recording ends — caller owns the lifecycle). If not provided,
//     the hook calls getUserMedia itself and releases the stream when
//     recording finishes.
//
// API:
//   startRecording(): Promise<boolean>   — true on success
//   stopRecording():  Promise<string>    — transcribed text ('' if empty)
//   cancel():         void               — discard in-flight recording
//   isRecording, isTranscribing, error
export function useWhisperRecorder({ getStream } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const stopResolverRef = useRef(null);
  const ownStreamRef = useRef(null);
  const startTimeRef = useRef(0);

  const releaseOwnStream = () => {
    if (ownStreamRef.current) {
      try {
        ownStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      ownStreamRef.current = null;
    }
  };

  // startRecording can take an explicit stream-getter at call time. This is
  // important on iOS Chrome where the closure-captured getStream from the
  // hook constructor sometimes has a stale reference; the call-site getter
  // always reads the latest ref.
  const startRecording = useCallback(async (callSiteGetStream) => {
    console.log('[Recorder] startRecording called');
    setError(null);

    // Cleanup any leftover session before starting fresh — fixes the
    // 'second hold doesn't record' failure mode where a prior recorder
    // is still alive in 'recording' state with stale handlers.
    if (
      recorderRef.current &&
      recorderRef.current.state !== 'inactive'
    ) {
      try {
        recorderRef.current.stop();
      } catch {}
    }
    recorderRef.current = null;
    if (ownStreamRef.current) {
      try {
        ownStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch {}
      ownStreamRef.current = null;
    }
    chunksRef.current = [];
    if (stopResolverRef.current) {
      // Discard any pending stop() resolver from a half-finished session
      stopResolverRef.current = null;
    }

    if (typeof MediaRecorder === 'undefined') {
      console.warn('[Recorder] MediaRecorder is not available');
      setError('Recording is not supported in this browser.');
      return false;
    }

    // Stream selection.
    // On iOS we always request a fresh getUserMedia. The intake-warmed
    // stream from micStreamRef goes inactive after ElevenLabs audio
    // playback releases the audio session — reusing it produces silent
    // recordings even though .active still reads true. The voice-test
    // page works precisely because it requests a fresh stream every time.
    const streamGetter =
      typeof callSiteGetStream === 'function' ? callSiteGetStream : getStream;
    let stream = null;

    if (!isIOS() && typeof streamGetter === 'function') {
      try {
        const result = streamGetter();
        const candidate = result instanceof Promise ? await result : result;
        if (candidate && candidate.active) {
          const tracks = candidate.getTracks();
          const allLive = tracks.every((t) => t.readyState === 'live');
          if (allLive) {
            console.log('[Recorder] Reusing existing stream (non-iOS)');
            stream = candidate;
          } else {
            console.warn(
              '[Recorder] Parent stream tracks not all live — requesting fresh'
            );
          }
        } else if (candidate) {
          console.warn(
            '[Recorder] Parent stream exists but is inactive — requesting fresh'
          );
        }
      } catch (e) {
        console.warn('[Recorder] streamGetter() threw:', e);
      }
    } else if (isIOS()) {
      console.log('[Recorder] iOS — always request fresh getUserMedia');
    }

    if (!stream) {
      try {
        console.log('[Recorder] Requesting fresh getUserMedia...');
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        ownStreamRef.current = stream;
        console.log(
          '[Recorder] Got fresh stream, tracks:',
          stream.getTracks().map((t) => `${t.kind}:${t.readyState}`)
        );
      } catch (e) {
        console.warn('[Recorder] Mic permission denied:', e);
        setError('Microphone permission denied.');
        return false;
      }
    }

    const mime = pickMimeType();
    console.log('[Recorder] Using mimeType:', mime || 'browser default');

    let recorder;
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
    } catch (e) {
      console.warn('[Recorder] Could not construct MediaRecorder:', e);
      setError('Could not start recorder.');
      releaseOwnStream();
      return false;
    }

    recorderRef.current = recorder;

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) {
        chunksRef.current.push(ev.data);
        console.log('[Recorder] Got chunk:', ev.data.size, 'bytes');
      }
    };

    recorder.onstart = () => console.log('[Recorder] MediaRecorder started');

    recorder.onstop = () => {
      console.log('[Recorder] MediaRecorder stopped');
      const finalMime = recorder.mimeType || mime || 'audio/mp4';
      const ext = getExtension(finalMime);
      const blob = new Blob(chunksRef.current, { type: finalMime });
      console.log(
        '[Whisper] Sending blob:',
        blob.size,
        'bytes, type:',
        finalMime,
        'extension:',
        ext
      );
      chunksRef.current = [];
      const resolver = stopResolverRef.current;
      stopResolverRef.current = null;
      if (resolver) resolver(blob);
    };

    recorder.onerror = (e) => {
      console.error('[Recorder] MediaRecorder error:', e?.error || e);
    };

    try {
      recorder.start(250); // emit chunks every 250ms — early data, robust on iOS
    } catch (e) {
      console.warn('[Recorder] recorder.start() failed:', e);
      setError('Could not start recording.');
      releaseOwnStream();
      return false;
    }

    startTimeRef.current = Date.now();
    setIsRecording(true);
    console.log('[Whisper] Recording started, mime:', recorder.mimeType);
    return true;
  }, [getStream]);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      setIsRecording(false);
      return '';
    }

    const elapsedMs = Date.now() - startTimeRef.current;
    const blob = await new Promise((resolve) => {
      stopResolverRef.current = resolve;
      try {
        recorder.stop();
      } catch (e) {
        console.warn('[Whisper] recorder.stop() failed:', e);
        resolve(null);
      }
    });

    setIsRecording(false);
    recorderRef.current = null;
    releaseOwnStream();

    // Skip absurdly short recordings (< 250ms) — usually accidental taps
    if (elapsedMs < 250) {
      console.log('[Whisper] Recording too short, ignoring');
      return '';
    }

    if (!blob || blob.size === 0) {
      console.log('[Whisper] Empty blob, nothing to transcribe');
      return '';
    }

    setIsTranscribing(true);
    try {
      console.log('[Whisper] Transcribing blob, size:', blob.size, 'type:', blob.type);
      const text = await transcribeAudio(blob);
      console.log('[Whisper] Transcript:', text);
      return text;
    } catch (e) {
      console.warn('[Whisper] Transcription failed:', e);
      setError(e?.message || 'Transcription failed.');
      return '';
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {}
    }
    chunksRef.current = [];
    stopResolverRef.current = null;
    recorderRef.current = null;
    releaseOwnStream();
    setIsRecording(false);
    setIsTranscribing(false);
  }, []);

  return {
    startRecording,
    stopRecording,
    cancel,
    isRecording,
    isTranscribing,
    error,
  };
}
